import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { sentenceCase } from 'change-case';
import { useState, useEffect } from 'react';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Button,
  Popover,
  Checkbox,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  InputAdornment,
  OutlinedInput,
  alpha,
} from '@mui/material';
import { useDispatch } from 'react-redux';

// components
import Label from '../components/label';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import api from '../api';

// sections
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
// mock
import USERLIST from '../_mock/user';
import { loadingStartAction, loadingStopAction } from 'src/redux/actions/loadingActions';
import { useNavigate } from 'react-router-dom';
import UserWalletUpdate from 'src/sections/@dashboard/wallet/UserWalletUpdate';
import { fShortenNumber } from 'src/utils/formatNumber';
import { row_per_page_data } from 'src/utils/commonUtils';
import styled from '@emotion/styled';
import moment from 'moment';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'Phone', label: 'Phone', alignRight: false },
  { id: 'wallet_balance', label: 'Wallet Balance', alignRight: false },
  { id: 'createdAt', label: 'Register Date', alignRight: false },
  { id: 'role', label: 'Role', alignRight: false },
  { id: 'isVerified', label: 'Verified', alignRight: false },
  { id: 'blocked', label: 'Blocked', alignRight: false },
  { id: 'view', label: 'View', alignRight: false },
  { id: 'action', label: 'Action', alignRight: false },
];

const StyledSearch = styled(OutlinedInput)(({ theme }) => ({
  width: 240,
  transition: theme.transitions.create(['box-shadow', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  '&.Mui-focused': {
    width: 320,
    boxShadow: theme.customShadows.z8,
  },
  '& fieldset': {
    borderWidth: `1px !important`,
    borderColor: `${alpha(theme.palette.grey[500], 0.32)} !important`,
  },
}));

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => _user.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function UserPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [open, setOpen] = useState(null);

  const [page, setPage] = useState(0);

  const [order, setOrder] = useState('asc');
  const [query, setQuery] = useState('');

  const [selected, setSelected] = useState([]);

  const [orderBy, setOrderBy] = useState('');

  const [filterName, setFilterName] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(row_per_page_data[0]);

  const [userData, setUserData] = useState({ totalUsers: 0, currentPage: 1, totalPages: 0, users: [] });

  const [selectedId, setSelectedId] = useState('');

  const handleOpenMenu = (event) => {
    setOpen(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // const handleSelectAllClick = (event) => {
  //   if (event.target.checked) {
  //     const newSelecteds = userData.map((n) => n.name);
  //     setSelected(newSelecteds);
  //     return;
  //   }
  //   setSelected([]);
  // };

  // const handleClick = (event, name) => {
  //   const selectedIndex = selected.indexOf(name);
  //   let newSelected = [];
  //   if (selectedIndex === -1) {
  //     newSelected = newSelected.concat(selected, name);
  //   } else if (selectedIndex === 0) {
  //     newSelected = newSelected.concat(selected.slice(1));
  //   } else if (selectedIndex === selected.length - 1) {
  //     newSelected = newSelected.concat(selected.slice(0, -1));
  //   } else if (selectedIndex > 0) {
  //     newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
  //   }
  //   setSelected(newSelected);
  // };

  const handleChangePage = (event, newPage) => {
    if (newPage + 1 > userData.currentPage) {
      getAllUsersList(newPage + 1);
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    getAllUsersList(1, parseInt(event.target.value, 10));
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const getAllUsersList = async (pg, limit) => {
    setQuery('');
    let endpoint = `/users?page=${pg}&limit=${limit || rowsPerPage}`;
    dispatch(loadingStartAction());
    const response = await api.get(endpoint);
    setUserData({
      totalUsers: response.data.totalUsers,
      currentPage: response.data.currentPage,
      totalPages: response.data.totalPages,
      users: pg === 1 ? response.data.users : [...userData.users, ...response.data.users],
    });
    dispatch(loadingStopAction());
  };

  const deleteUser = async () => {
    handleCloseMenu();
    let deletedData = userData?.users.filter((item) => item._id !== selectedId);
    setUserData((prevUserData) => ({
      ...prevUserData,
      users: deletedData,
    }));
    await api.delete(`/users/${selectedId}`);
  };

  const blockUser = async (selectedItemId) => {
    handleCloseMenu();
    const updateData = userData?.users.map((item) =>
      item._id === selectedItemId ? { ...item, isBlocked: !item.isBlocked } : item
    );
    setUserData((prevUserData) => ({
      ...prevUserData,
      users: updateData,
    }));

    await api.put(`/users/${selectedItemId}/block`);
  };

  const userDetailsNavigate = (item) => {
    navigate(`${item._id}`);
  };

  useEffect(() => {
    getAllUsersList(1);
  }, []);

  const getAllFilteredUsers = async () => {
    // The table slices by page index. Searching while on a later page left the
    // results sliced away and the table looking empty.
    setPage(0);
    let endpoint = `/users/search?query=${query}`;
    dispatch(loadingStartAction());
    const response = await api.get(endpoint);

    setUserData({
      totalUsers: response.data.results.length,
      currentPage: 1,
      totalPages: 1,
      users: response.data.results,
    });

    dispatch(loadingStopAction());
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - userData?.totalUsers) : 0;

  const filteredFinalData = applySortFilter(
    userData?.users ? userData?.users : [],
    getComparator(order, orderBy),
    filterName
  );

  const isNotFound = !filteredFinalData.length && !!filterName;

  return (
    <>
      <Helmet>
        <title> User | Minimal UI </title>
      </Helmet>
      <StyledSearch
        value={query}
        onChange={(e) => {
          e.target.value ? setQuery(e.target.value) : getAllUsersList(1);
        }}
        placeholder="Search user..."
        startAdornment={
          <InputAdornment position="start">
            <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
          </InputAdornment>
        }
      />

      <Button style={{ marginLeft: 10 }} variant="outlined" size="large" onClick={getAllFilteredUsers}>
        SEARCH
      </Button>

      <Container>
        <Card>
          {/* <UserListToolbar numSelected={selected.length} filterName={filterName} onFilterName={handleFilterByName} /> */}
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={userData?.totalUsers}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  // onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredFinalData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const { _id, name, wallet_balance, role, status, email, pic, isBlocked, isAdmin, createdAt } = row;
                    const selectedUser = selected.indexOf(name) !== -1;

                    return (
                      <TableRow hover key={_id} tabIndex={-1} role="checkbox" selected={selectedUser}>
                        <TableCell padding="checkbox"></TableCell>

                        <TableCell component="th" scope="row" padding="none">
                          <Stack
                            spacing={2}
                            direction="row"
                            alignItems="center"
                            style={{
                              color: 'blue',
                              cursor: 'pointer',
                            }}
                            onClick={() => userDetailsNavigate(row)}
                          >
                            <Avatar alt={name} src={pic} />
                            <Typography variant="subtitle2" noWrap>
                              {name}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">{email}</TableCell>
                        <TableCell align="left">₹ {fShortenNumber(wallet_balance) || 0}</TableCell>
                        <TableCell align="left">{moment(createdAt).format('llll')}</TableCell>

                        <TableCell align="left">{'role'}</TableCell>

                        <TableCell align="left">{isAdmin ? 'Admin' : 'User'}</TableCell>

                        <TableCell align="left">
                          <Label
                            onClick={() => blockUser(row._id)}
                            style={{
                              cursor: 'pointer',
                            }}
                            color={isBlocked ? 'error' : 'success'}
                          >
                            {isBlocked ? 'YES' : 'NO'}
                          </Label>
                        </TableCell>

                        <TableCell align="left">
                          <Button onClick={() => userDetailsNavigate(row)} size="small" variant="outlined">
                            View
                          </Button>
                        </TableCell>

                        <TableCell align="right">
                          <Stack direction="row">
                            <UserWalletUpdate
                              userData={userData}
                              setUserData={setUserData}
                              getAllUsersList={getAllUsersList}
                              userId={_id}
                              type="credit"
                            />
                            <UserWalletUpdate
                              userData={userData}
                              setUserData={setUserData}
                              getAllUsersList={getAllUsersList}
                              userId={_id}
                              type="debit"
                            />
                            <IconButton
                              size="large"
                              color="inherit"
                              onClick={(e) => {
                                setSelectedId(_id);
                                handleOpenMenu(e);
                              }}
                            >
                              <Iconify icon={'eva:more-vertical-fill'} />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" paragraph>
                            Not found
                          </Typography>

                          <Typography variant="body2">
                            No results found for &nbsp;
                            <strong>&quot;{filterName}&quot;</strong>.
                            <br /> Try checking for typos or using complete words.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={row_per_page_data}
            component="div"
            count={userData.totalUsers || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        {/* <MenuItem>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          Edit
        </MenuItem> */}

        <MenuItem onClick={() => deleteUser()} sx={{ color: 'error.main' }}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Delete
        </MenuItem>

        <MenuItem onClick={() => blockUser(selectedId)} sx={{ color: 'error.main' }}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Block
        </MenuItem>
      </Popover>
    </>
  );
}
